// ---------------------------------------------------------------------------
// Auth — สมัคร/เข้าสู่ระบบด้วย "อีเมล + รหัสผ่าน" ผ่าน PaymentBroker (แบบ AITalk)
//  - สิทธิ์ super_user ยึดจาก GET profile ของ Backend เท่านั้น (ค่าใน localStorage แก้เองได้ ห้ามเชื่อ)
//  - โหมดทดลอง: บัญชีอยู่ในฐานจำลอง (localStorage) อีเมลที่ขึ้นต้นด้วย "admin" = super_user
// ---------------------------------------------------------------------------
import { ENDPOINTS, CAPTCHA, IS_MOCK, USER_TYPE, AUTH_EVENT } from "./config";
import { http, ApiError, setToken, setStoredProfile, getStoredProfile, clearSession } from "./apiClient";
import { mockFb } from "./mockFb";
import { fk, newId, nowIso } from "./db";
import { users } from "./users";

const emit = () => window.dispatchEvent(new Event(AUTH_EVENT));
export const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
export const passwordIssues = (p) => {
  const s = String(p || "");
  const out = [];
  if (s.length < 8) out.push("อย่างน้อย 8 ตัวอักษร");
  if (!/[A-Za-z]/.test(s)) out.push("มีตัวอักษรภาษาอังกฤษ");
  if (!/\d/.test(s)) out.push("มีตัวเลข");
  return out;
};

const DEFAULT_PHONE = "0000000000";
const norm = (e) => String(e || "").trim().toLowerCase();

/** ตัวตนจาก profile ของ PaymentBroker (ตาม identityFromProfile ของ AITalk) */
export function identityFromProfile(d) {
  d = d || {};
  const cust = d.customer || {};
  const email = cust.email || d.email || "";
  const uid = cust.uid || d.uid || d.id || d._id || email;
  const name = cust.name_th || d.full_name || (email ? String(email).split("@")[0] : "") || "ผู้ใช้";
  return { uid: String(uid || ""), email, displayName: String(name).slice(0, 40), customerUid: cust.uid || "", user_type: d.user_type === USER_TYPE.superUser ? USER_TYPE.superUser : USER_TYPE.user };
}

async function finishLogin(identity) {
  try {
    await users.ensure(identity);
  } catch (e) {
    clearSession(); // บัญชีถูกระงับ/ฐานข้อมูลล่ม — ไม่ค้างสถานะล็อกอินครึ่งๆ กลางๆ
    throw new ApiError(e.message);
  }
  const profile = { ...identity, loggedInAt: nowIso() };
  setStoredProfile(profile);
  emit();
  return profile;
}

export const authService = {
  login: async ({ email, password, remember = true, captchaToken }) => {
    if (!validEmail(email) || !password) throw new ApiError("กรุณากรอกอีเมลและรหัสผ่านให้ถูกต้อง");
    if (IS_MOCK) {
      const acc = await mockFb.get(`_auth/${fk(norm(email))}`);
      if (!acc) throw new ApiError("ไม่พบบัญชีนี้ (โหมดทดลอง: สมัครสมาชิกก่อน)");
      if (acc.password !== btoa(unescape(encodeURIComponent(password)))) throw new ApiError("อีเมลหรือรหัสผ่านไม่ถูกต้อง");
      setToken(`mock-${acc.uid}`);
      return finishLogin({ uid: acc.uid, email: acc.email, displayName: acc.displayName, customerUid: "", user_type: acc.userType });
    }
    const result = await http.post(ENDPOINTS.login, { email: email.trim(), password: btoa(password), remember, token: captchaToken || CAPTCHA.masterToken });
    if (!result.status) throw new ApiError(result.message, result);
    setToken(result.data.token);
    const prof = await http.get(ENDPOINTS.profile);
    if (!prof.status) {
      clearSession();
      throw new ApiError(prof.message || "อ่านข้อมูลบัญชีไม่สำเร็จ");
    }
    return finishLogin(identityFromProfile({ email: email.trim(), ...prof.data }));
  },

  /** สมัครด้วยอีเมล + รหัสผ่านเท่านั้น แล้วเข้าสู่ระบบให้ทันที */
  register: async ({ email, password, displayName, captchaToken }) => {
    if (!validEmail(email)) throw new ApiError("รูปแบบอีเมลไม่ถูกต้อง");
    const issues = passwordIssues(password);
    if (issues.length) throw new ApiError("รหัสผ่านต้อง" + issues.join(" "));
    const name = (displayName || "").trim() || email.split("@")[0];
    if (IS_MOCK) {
      const key = `_auth/${fk(norm(email))}`;
      const uid = newId("u");
      const created = await mockFb.createOnly(key, {
        uid, email: norm(email), displayName: name, password: btoa(unescape(encodeURIComponent(password))),
        userType: norm(email).startsWith("admin") ? USER_TYPE.superUser : USER_TYPE.user, createdAt: nowIso(),
      });
      if (!created) throw new ApiError("อีเมลนี้สมัครไว้แล้ว กรุณาเข้าสู่ระบบ");
      return authService.login({ email, password });
    }
    // PaymentBroker ต้องมีเบอร์ (ไม่มีแล้ว login ไม่ได้ "Invalid credentials or inactive account") แต่ MixAI สมัครด้วยอีเมลอย่างเดียว
    // -> ใส่เบอร์ default ตามที่ผู้ใช้สั่ง (2026-10-17) ; backend ไม่บังคับเบอร์ห้ามซ้ำ
    const result = await http.post(ENDPOINTS.register, { name_th: name, name_en: name, email: email.trim(), password, phone: DEFAULT_PHONE, company_address: "" });
    if (!result.status) {
      const msg = result.message === "Create customer failed" ? "ระบบชำระเงินสร้างบัญชีลูกค้าไม่สำเร็จ กรุณาลองใหม่ภายหลัง หรือติดต่อทีมงาน" : result.message;
      throw new ApiError(msg, result);
    }
    return authService.login({ email, password, captchaToken });
  },

  forgotPassword: async (email) => {
    if (!validEmail(email)) throw new ApiError("รูปแบบอีเมลไม่ถูกต้อง");
    if (IS_MOCK) return { sent: true };
    const result = await http.post(ENDPOINTS.resetPassword, { email: email.trim() });
    if (!result.status) throw new ApiError(result.message, result);
    return result.data;
  },

  /** ถาม Backend ว่า token นี้เป็น super_user จริงไหม (RequireSuperUser เรียกทุกครั้งที่เข้า /admin) */
  verifySuperUser: async () => {
    const p = getStoredProfile();
    if (!p) return false;
    if (IS_MOCK) {
      const acc = await mockFb.get(`_auth/${fk(norm(p.email))}`);
      return acc?.userType === USER_TYPE.superUser;
    }
    const result = await http.get(ENDPOINTS.profile);
    return !!result.status && result.data?.user_type === USER_TYPE.superUser;
  },

  logout: async () => {
    if (!IS_MOCK) {
      try {
        await http.get(ENDPOINTS.logout);
      } catch (e) {
        /* ignore */
      }
    }
    clearSession();
  },

  currentProfile: () => getStoredProfile(),
};
